import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSubject } from "../../context/SubjectContext";
import { useCenter } from "../../context/CenterContext";
import "../../components/MarkSubmission/MarkSubmissionTable.css";
import styles from "./MarksSubmissionViewPage.module.css";
import MarkSubmissionViewHeader from "../../components/MarkSubmission/MarkSubmissionViewHeader";
import { Toaster } from "react-hot-toast";
import { useApi } from "../../context/ApiClient";
import {
  getQualificationTitle,
  getCenterType,
  checkunitIsConsortiaUnit,
} from "../../utils/utils";
import { useConsortiaCenter } from "../../context/ConsortiaCenterContext";
import { useUnits } from "../../context/UnitsContext";

const MarkSubmissionViewPage = () => {
  const location = useLocation();
  const { unit } = useParams();
  const api = useApi(process.env.REACT_APP_AMS_BASE_URL);
  const { sessionId, mode = "view" } = location.state || {};
  const unitcode = unit.split(" ")[0];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { consortiaCentres = [] } = useConsortiaCenter();
  const [currentCentreType, setCurrentCentreType] = useState("");
  const { consortiaUnits = [] } = useUnits();
  

  const { centerNumber } = useCenter();
  const { subjectData, subjects } = useSubject();

  const subjectId = subjectData?.selectedSubjectId;

  // Extract assessment code from URL param
  const assessmentCode = unit?.split("_")[0]?.toUpperCase();
  const centreNumber = centerNumber;

  const consortiaUnitcode =
    String(unit ?? "")
      .toUpperCase()
      .match(/^[A-Z0-9]+/)?.[0] || "";

  const isConsortiaUnit = checkunitIsConsortiaUnit(
    consortiaCentres,
    centerNumber,
    sessionId,
    consortiaUnitcode,
  );

  useEffect(() => {
    if (!centreNumber || !subjectId || !sessionId || !assessmentCode) {
      setError("Missing required parameters.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      const centreType =
        getCenterType(
          consortiaCentres,
          Number(centerNumber),
          subjectData?.selectedSubject,
          subjectData?.selectedSubjectId,
        ) || "";
      setCurrentCentreType(centreType);

      const endpoint =
        (centreType === "Lead" || centreType === "Rest") && isConsortiaUnit
          ? "consortia/getConsortiaCandidateDetails"
          : "getCandidateDetails";

      const params =
        (centreType === "Lead" || centreType === "Rest") && isConsortiaUnit
          ? {
              centreNumber,
              sessionId,
              assessmentCode,
            }
          : {
              centreNumber,
              subjectId,
              sessionId,
              assessmentCode,
            };

      try {
        const res = await api.get(endpoint, { params });

        if (res.data?.isSuccess && Array.isArray(res.data.data)) {
          setRows(res.data.data);
        } else {
          setError(res.data?.message || "No candidate details found.");
        }
      } catch (err) {
        setError("No candidate details found.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [centreNumber, subjectId, sessionId, assessmentCode, api]);

  if (loading) return <p>Loading candidate details…</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  return (
    <div className={styles.marksentrypage}>
      <Toaster
        position="top-right"
        containerStyle={{ marginTop: "6rem" }} // avoid overlap with fixed header
        toastOptions={{
          // default style applied to every toast
          style: {
            width: "400px", // <- increased width
            maxWidth: "calc(100vw - 2rem)", // <- responsive cap for small screens
            borderRadius: "4px",
            padding: "1em",
            fontFamily:
              "'Open Sans', 'Source Sans 3', Arial, Helvetica, sans-serif",
            boxShadow:
              "rgba(0, 0, 0, 0.14) 0px 8px 12px -4px, rgba(0, 0, 0, 0.24) 0px 4px 6px -2px",
          },
          error: {
            style: {
              backgroundColor: "#FFF4F7",
              border: "1px solid #DD1F36",
              color: "#DD1F36",
            },
          },
          success: {
            style: {
              backgroundColor: "#f5faf5",
              border: "1px solid #2B8000",
              color: "#2B8000",
            },
          },
        }}
      />

      <div className={styles.xcontainer}>
        <section
          className="section mt-0 py-0"
          style={{ paddingRight: "0rem !important" }}
        >
          <div className="pt-3 subject-header-fixed">
            <p className="is-size-6 has-text-weight-bold">
              {getQualificationTitle(subjects, subjectData?.selectedSubjectId)}
            </p>
            <h1 className="mb-0">
              <span className={styles.subjectName}>
                {subjectData?.selectedSubject}
              </span>{" "}
              <span className={styles.subjectCode}>
                {subjectData?.selectedSubjectCode}
              </span>
            </h1>

            <p className="xbox xnotification is-info mt-0">
              Total marks submission
            </p>
          </div>
        </section>
      </div>

      <div style={{ paddingLeft: "25px" }}>
        <MarkSubmissionViewHeader rows={rows} />
      </div>
      <div style={{ paddingLeft: "25px" }}>
        <div className="table-container">
          <table className="table is-hoverable mark-table">
            <colgroup>
              <col className={styles.colStatus} />
              <col className="col-number" />
              <col className="col-name" />
              <col className="col-absent" />
              <col className="col-marks" />
              <col className="col-assignment" />
            </colgroup>
            <thead>
              <tr>
                <th>Status</th>
                {((currentCentreType === "Lead" && isConsortiaUnit) ||
                  (currentCentreType === "Rest" &&
                    mode === "view" &&
                    isConsortiaUnit)) && <th>Centre Number</th>}
                <th>Candidate number</th>
                <th>Candidate name</th>
                <th>Absent?</th>
                <th>Total number of criteria achieved</th>
                <th>Assignment</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const complete =
                  row.markAsAbsent ||
                  (!row.markAsAbsent &&
                    row.totalCriteriaAchieved !== null &&
                    (row.assignment || row.assignmentId));

                return (
                  <tr key={row.candidateNumber}>
                    <td className={styles.colStatus}>
                      {complete ? (
                        <svg
                          style={{ fill: "#2B8000" }}
                          focusable="false"
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          width={24}
                          height={24}
                        >
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.88-11.71L10 14.17l-1.88-1.88a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l2.59 2.59c.39.39 1.02.39 1.41 0L17.3 9.7c.39-.39.39-1.02 0-1.41-.39-.39-1.03-.39-1.42 0z" />
                        </svg>
                      ) : (
                        <svg
                          style={{ fill: "#D9221C" }}
                          focusable="false"
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          width={24}
                          height={24}
                        >
                          <path d="M12 7c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1s-1-.45-1-1V8c0-.55.45-1 1-1zm-.01-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm1-3h-2v-2h2v2z" />
                        </svg>
                      )}
                    </td>
                    {((currentCentreType === "Lead" && isConsortiaUnit) ||
                      (currentCentreType === "Rest" &&
                        mode === "view" &&
                        isConsortiaUnit)) && (
                      <td className="col-number">{row.centreNumber}</td>
                    )}
                    <td className="col-number">{row.candidateNumber}</td>
                    <td className="col-name">{row.candidateName}</td>
                    <td className="col-absent">
                      {row.markAsAbsent ? "Yes" : "No"}
                    </td>
                    <td className="col-marks">
                      <div className="mark-input-wrapper">
                        {row.markAsAbsent
                          ? "-"
                          : `${row.totalCriteriaAchieved} / ${row.totalMarks}`}
                      </div>
                    </td>
                    <td className="col-assignment">{row.assignment || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MarkSubmissionViewPage;
